import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

interface JitsiRoomProps {
    domain: string;
    roomName: string;
    displayName?: string;
    subject?: string;
    jwt?: string | null;
    onClose: () => void;
}

// external_api.js wird pro Domain nur einmal geladen (Promise-Cache).
const scriptPromises: Record<string, Promise<void>> = {};

const loadExternalApi = (domain: string): Promise<void> => {
    if (window.JitsiMeetExternalAPI) {
        return Promise.resolve();
    }
    if (!scriptPromises[domain]) {
        scriptPromises[domain] = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://${domain}/external_api.js`;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                delete scriptPromises[domain];
                reject(new Error('Jitsi konnte nicht geladen werden.'));
            };
            document.body.appendChild(script);
        });
    }
    return scriptPromises[domain];
};

const JitsiRoom: React.FC<JitsiRoomProps> = ({ domain, roomName, displayName, subject, jwt, onClose }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        let api: InstanceType<NonNullable<Window['JitsiMeetExternalAPI']>> | null = null;
        let cancelled = false;

        loadExternalApi(domain)
            .then(() => {
                if (cancelled || !containerRef.current || !window.JitsiMeetExternalAPI) {
                    return;
                }
                api = new window.JitsiMeetExternalAPI(domain, {
                    roomName,
                    parentNode: containerRef.current,
                    jwt: jwt ?? undefined,
                    userInfo: displayName ? { displayName } : undefined,
                    configOverwrite: {
                        prejoinPageEnabled: false,
                        disableDeepLinking: true,
                        subject: subject ?? '',
                    },
                    interfaceConfigOverwrite: {
                        SHOW_JITSI_WATERMARK: false,
                        SHOW_BRAND_WATERMARK: false,
                    },
                });
                api.addEventListener('readyToClose', () => onCloseRef.current());
            })
            .catch(() => {
                if (!cancelled) {
                    onCloseRef.current();
                }
            });

        return () => {
            cancelled = true;
            if (api) {
                api.dispose();
            }
        };
    }, [domain, roomName, displayName, subject, jwt]);

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: { xs: 'calc(100vh - 160px)', md: 'calc(100vh - 180px)' },
                minHeight: 360,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'black',
            }}
        >
            <Stack
                alignItems="center"
                justifyContent="center"
                spacing={2}
                sx={{ position: 'absolute', inset: 0, color: 'common.white' }}
            >
                <CircularProgress color="inherit" />
                <Typography variant="body2">Konferenz wird geladen …</Typography>
            </Stack>
            <Box ref={containerRef} sx={{ position: 'relative', width: '100%', height: '100%' }} />
        </Box>
    );
};

export default JitsiRoom;
